import ProductRecordForm from "@/components/ProductRecordForm";
import * as Dialog from "@radix-ui/react-dialog";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import { IS_LOCAL_DEMO } from "@/components/demo/DemoPackageSwitch";
import {
  Package,
  FlaskConical,
  Plus,
  X,
  Pencil,
  Search,
  Beaker,
  Truck,
  Trash2,
  Save,
  Loader2,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  GripVertical,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  Weight,
  Camera,
  Upload,
  FileText,
  ExternalLink,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProductSummary {
  id: number;
  product_name: string;
  product_id: string;
  ingredient_ratios: string | null;
  certifications: string[] | string | null;
  size_categories: string[] | string | null;
  size_category_names: string[];
  ingredient_count: number;
  density_lbs_per_cuft: number | null;
}

interface IngredientDetail {
  id: number;
  name: string;
  type: string;
  cost_text: string | null;
  cost_per_ton: number | null;
  supplier_name: string | null;
  density_lbs_per_cuft: number | null;
}

interface ProductIngredient {
  id: number;
  product_cache_id: number;
  ingredient_id: number;
  ratio_percent: number | null;
  weight_per_batch_lbs: number | null;
  batch_total_lbs: number | null;
  ratio_type: string | null;
  sort_order: number;
  ingredient: IngredientDetail;
}

interface ProductDetail {
  notes?: string | null;
  id: number;
  product_name: string;
  product_id: string;
  ingredients_list?: string | null;
  ingredient_ratios: string | null;
  certifications: string[] | string | null;
  size_categories: string[] | string | null;
  size_category_names: string[];
  density_lbs_per_cuft: number | null;
  illustration_url?: string | null;
  documents?: any[];
}

interface IngredientDocument {
  name: string;
  url: string;
  path: string;
  type: string;
  uploadedAt: string;
}

interface IngredientRow {
  id: number;
  ingr_id?: string | null;
  name: string;
  type: string;
  cost_text: string | null;
  cost_per_ton: number | null;
  supplier_name: string | null;
  supplier_contact: string | null;
  is_active: boolean;
  product_count: number;
  density_lbs_per_cuft: number | null;
  image_url?: string | null;
  documents?: IngredientDocument[];
}

interface EditableIngredient {
  key: string;
  ingredientId: number | null;
  ratioPercent: number | null;
  weightPerBatchLbs: number | null;
  batchTotalLbs: number | null;
  ratioType: string;
}

interface IngredientForm {
  ingrId: string;
  name: string;
  type: string;
  costText: string;
  costPerTon: string;
  supplierName: string;
  supplierContact: string;
  densityLbsPerCuft: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RATIO_COLORS_HEX = [
  "#2d6a2e", "#059669", "#d97706", "#0284c7", "#f43f5e",
  "#7c3aed", "#f97316", "#0d9488", "#6366f1", "#65a30d",
];

type ProductSortField = "product_name" | "product_id" | "ingredient_count";
type IngredientSortField = "ingr_id" | "name" | "type" | "cost_per_ton" | "supplier_name" | "product_count" | "density_lbs_per_cuft";
type SortDir = "asc" | "desc";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let _keyCounter = 0;
function uniqueKey() {
  return `ik_${++_keyCounter}_${Date.now()}`;
}

function parseCertifications(raw: string[] | string | null): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
  } catch { /* fall through */ }
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Spinner({ className = "h-5 w-5" }: { className?: string }) {
  return <Loader2 className={`animate-spin ${className}`} />;
}

function CertBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
      {label}
    </span>
  );
}

function Badge({
  children,
  variant = "default",
}: {
  children: React.ReactNode;
  variant?: "default" | "success" | "warning" | "info" | "muted";
}) {
  const base = "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium";
  const variants: Record<string, string> = {
    default: "bg-forest-600/20 text-forest-400 border border-forest-600/30",
    success: "bg-emerald-600/20 text-emerald-400 border border-emerald-600/30",
    warning: "bg-amber-600/20 text-amber-400 border border-amber-600/30",
    info: "bg-sky-600/20 text-sky-400 border border-sky-600/30",
    muted: "bg-secondary text-muted-foreground border border-border",
  };
  return <span className={`${base} ${variants[variant]}`}>{children}</span>;
}

function SortIcon<T extends string>({ field, sortField, sortDir }: { field: T; sortField: T; sortDir: SortDir }) {
  if (field !== sortField) return <ArrowUpDown size={12} className="text-muted-foreground/40" />;
  return sortDir === "asc" ? <ArrowUp size={12} className="text-forest-500" /> : <ArrowDown size={12} className="text-forest-500" />;
}

// ---------------------------------------------------------------------------
// Slide-Over Panel (Product Detail)
// ---------------------------------------------------------------------------

function SlideOverPanel({
  open, onClose, productId, token, allIngredients, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  productId: number | null;
  token: string | null;
  allIngredients: IngredientRow[];
  onSaved: () => void;
}) {
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [editingRecord, setEditingRecord] = useState(false);
  const { user } = useAuth();
  const canManage = ["manager", "admin", "owner", "platform_admin"].includes(user?.role || "");
  const [ingredients, setIngredients] = useState<ProductIngredient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editRows, setEditRows] = useState<EditableIngredient[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [editingDensity, setEditingDensity] = useState(false);
  const [densityValue, setDensityValue] = useState("");
  const [savingDensity, setSavingDensity] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!productId || !token) return;
    setEditingRecord(false);
    setLoading(true);
    setError(null);
    setEditing(false);
    setSaveSuccess(false);
    apiFetch(`/api/ops/products/${productId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (!res.ok) throw new Error('Request failed'); return res.json(); })
      .then((data) => { setProduct(data.product); setIngredients(data.ingredients || []); })
      .catch((err) => setError(err.message || "Failed to load product details"))
      .finally(() => setLoading(false));
  }, [productId, token]);

  function startEditing() {
    const rows: EditableIngredient[] = ingredients.map((ing) => ({
      key: uniqueKey(), ingredientId: ing.ingredient_id, ratioPercent: ing.ratio_percent,
      weightPerBatchLbs: ing.weight_per_batch_lbs, batchTotalLbs: ing.batch_total_lbs,
      ratioType: ing.ratio_type || "percent",
    }));
    if (rows.length === 0) rows.push({ key: uniqueKey(), ingredientId: null, ratioPercent: null, weightPerBatchLbs: null, batchTotalLbs: null, ratioType: "percent" });
    setEditRows(rows);
    setEditing(true);
    setSaveError(null);
    setSaveSuccess(false);
  }

  function addRow() {
    setEditRows((prev) => [...prev, { key: uniqueKey(), ingredientId: null, ratioPercent: null, weightPerBatchLbs: null, batchTotalLbs: null, ratioType: "percent" }]);
  }
  function removeRow(key: string) { setEditRows((prev) => prev.filter((r) => r.key !== key)); }
  function updateRow(key: string, field: keyof EditableIngredient, value: any) {
    setEditRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  }

  async function saveRecipe() {
    if (!productId || !token) return;
    setSaving(true); setSaveError(null); setSaveSuccess(false);
    const payload = editRows.filter((r) => r.ingredientId != null).map((r) => ({
      ingredientId: r.ingredientId, ratioPercent: r.ratioPercent,
      weightPerBatchLbs: r.weightPerBatchLbs, batchTotalLbs: r.batchTotalLbs, ratioType: r.ratioType,
    }));
    try {
      const res = await apiFetch(`/api/ops/products/${productId}/ingredients`, {
        method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ingredients: payload }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `Save failed (${res.status})`); }
      const detailRes = await apiFetch(`/api/ops/products/${productId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!detailRes.ok) throw new Error('Request failed');
      const detailData = await detailRes.json();
      setProduct(detailData.product); setIngredients(detailData.ingredients || []);
      setEditing(false); setSaveSuccess(true); onSaved();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) { setSaveError(err.message || "Failed to save recipe"); }
    finally { setSaving(false); }
  }

  function handleBackdropClick(e: React.MouseEvent) {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
  }

  function startEditDensity() {
    setDensityValue(product?.density_lbs_per_cuft != null ? String(product.density_lbs_per_cuft) : "");
    setEditingDensity(true);
  }

  async function saveDensity() {
    if (!productId || !token) return;
    setSavingDensity(true);
    try {
      const res = await apiFetch(`/api/ops/products/${productId}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ densityLbsPerCuft: densityValue ? Number(densityValue) : null }),
      });
      if (!res.ok) throw new Error("Failed to save density");
      const data = await res.json();
      setProduct((prev) => prev ? { ...prev, density_lbs_per_cuft: data.product.density_lbs_per_cuft } : prev);
      setEditingDensity(false);
      onSaved();
    } catch { setSaveError("Failed to save density"); }
    finally { setSavingDensity(false); }
  }

  async function handleProductPhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !productId || !token) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiFetch(`/api/ops/products/${productId}/image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setProduct((prev) => prev ? { ...prev, illustration_url: data.url || data.illustration_url } : prev);
      onSaved();
    } catch (err: any) { setSaveError(err.message || "Failed to upload photo"); }
    finally { setUploadingPhoto(false); if (photoInputRef.current) photoInputRef.current.value = ""; }
  }

  const totalRatio = editRows.reduce((sum, r) => sum + (r.ratioPercent || 0), 0);
  const certs = product ? parseCertifications(product.certifications) : [];
  const sizeNames = product?.size_category_names || [];

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={handleBackdropClick}
      />
      <div
        ref={panelRef}
        style={IS_LOCAL_DEMO ? { top: 46, height: "calc(100% - 46px)" } : undefined}
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-2xl bg-background border-l border-border shadow-2xl transform transition-transform duration-300 ease-in-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-border">
            <div className="min-w-0">
              {product ? (
                <>
                  <h2 className="text-lg font-semibold text-foreground truncate">{product.product_name}</h2>
                  <p className="text-sm text-muted-foreground font-mono">{product.product_id}</p>
                </>
              ) : <div className="h-6 w-48 bg-secondary rounded animate-pulse" />}
            </div>
            <button aria-label="Close details" onClick={onClose} className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading && <div className="flex items-center justify-center py-20"><Spinner className="h-8 w-8 text-forest-500" /></div>}
            {error && (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400">
                <AlertCircle className="h-5 w-5 flex-shrink-0" /><p className="text-sm">{error}</p>
              </div>
            )}

            {!loading && !error && product && (
              <>
                <section className="mb-6 p-4 border border-border rounded-xl">
                  <div className="flex items-center justify-between mb-3"><h3 className="font-semibold">Product record</h3>{canManage && !editingRecord && <button onClick={() => setEditingRecord(true)} className="text-sm text-forest-600 inline-flex items-center gap-1"><Pencil size={14}/> Edit product</button>}</div>
                  {editingRecord ? <ProductRecordForm key={product.id} product={product} onCancel={() => setEditingRecord(false)} onSaved={(saved) => { setProduct({...product, ...saved}); setEditingRecord(false); onSaved(); }}/> : <><p className="text-xs font-medium text-muted-foreground mb-1">Notes</p><p className="text-sm whitespace-pre-wrap break-words">{product.notes || "No product notes yet."}</p></>}
                </section>
                {/* Product Photo */}
                {(!IS_LOCAL_DEMO || product.illustration_url) && <div className="mb-6">
                  <div className="relative group">
                    {product.illustration_url ? (
                      <img
                        src={product.illustration_url}
                        alt={product.product_name}
                        className="w-full max-w-[200px] h-auto rounded-xl border border-border object-cover"
                      />
                    ) : (
                      <div className="w-full max-w-[200px] h-[200px] rounded-xl border border-dashed border-border bg-secondary/30 flex flex-col items-center justify-center text-muted-foreground">
                        <Camera className="h-8 w-8 mb-2 opacity-40" />
                        <span className="text-xs">No photo</span>
                      </div>
                    )}
                    {!IS_LOCAL_DEMO && <><input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleProductPhotoUpload}
                    />
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-lg transition-colors disabled:opacity-50"
                    >
                      {uploadingPhoto ? <Spinner className="h-3.5 w-3.5" /> : <Camera size={13} />}
                      {uploadingPhoto ? "Uploading..." : "Change Photo"}
                    </button></>}
                  </div>
                </div>}

                {/* Certifications */}
                {certs.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Certifications</h3>
                    <div className="flex flex-wrap gap-2">
                      {certs.map((c, i) => <CertBadge key={i} label={c} />)}
                    </div>
                  </div>
                )}

                {/* Size categories - resolved names */}
                {sizeNames.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Size Categories</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {sizeNames.map((name, i) => (
                        <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Linked Ingredient IDs (from Airtable) */}
                {product.ingredients_list && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Linked Ingredients (Airtable)</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {(typeof product.ingredients_list === 'string' ? product.ingredients_list.split(',').map((s: string) => s.trim()) : []).map((recId: string, i: number) => (
                        <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono bg-secondary text-muted-foreground border border-border">
                          {recId}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Product Density */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                      <Weight size={14} /> Product Density
                    </h3>
                    {!editingDensity && canManage && (
                      <button onClick={startEditDensity} className="text-xs text-forest-400 hover:text-forest-300 transition-colors flex items-center gap-1">
                        <Pencil size={12} /> Edit
                      </button>
                    )}
                  </div>
                  {editingDensity ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number" min="0" step="0.1" value={densityValue}
                        onChange={(e) => setDensityValue(e.target.value)}
                        placeholder="lbs/ft³"
                        className="w-32 bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground tabular-nums focus:outline-none focus:ring-2 focus:ring-forest-600/50 focus:border-forest-600"
                        autoFocus
                      />
                      <span className="text-xs text-muted-foreground">lbs/ft³</span>
                      <button onClick={saveDensity} disabled={savingDensity} className="p-1.5 text-forest-400 hover:text-forest-300 transition-colors">
                        {savingDensity ? <Spinner className="h-4 w-4" /> : <Save size={14} />}
                      </button>
                      <button onClick={() => setEditingDensity(false)} className="p-1.5 text-muted-foreground hover:text-foreground transition-colors">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg bg-secondary/50 border border-border">
                      {product.density_lbs_per_cuft != null ? (
                        <span className="text-lg font-semibold text-foreground tabular-nums">{product.density_lbs_per_cuft} <span className="text-sm font-normal text-muted-foreground">lbs/ft³</span></span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Not measured yet</span>
                      )}
                    </div>
                  )}
                </div>

                {saveSuccess && (
                  <div className="mb-4 flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">
                    <CheckCircle className="h-4 w-4" /> Recipe saved successfully
                  </div>
                )}

                {/* Ingredient Ratios (from Airtable text field) */}
                {product.ingredient_ratios && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-foreground mb-2">Recipe Notes</h3>
                    <div className="p-3 rounded-lg bg-secondary/50 border border-border">
                      <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-sans leading-relaxed">
                        {product.ingredient_ratios}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Ingredient Breakdown */}
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-foreground">Ingredient Breakdown</h3>
                  {!editing && canManage && (
                    <button onClick={startEditing} className="flex items-center gap-1.5 text-sm text-forest-400 hover:text-forest-300 transition-colors">
                      <Pencil className="h-3.5 w-3.5" /> Edit Recipe
                    </button>
                  )}
                </div>

                {!editing ? (
                  <>
                    {ingredients.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <FlaskConical className="h-10 w-10 mx-auto mb-3 opacity-40" />
                        <p className="text-sm">No ingredients configured</p>
                        <button onClick={startEditing} className="mt-3 text-sm text-forest-400 hover:text-forest-300 transition-colors">Add ingredients</button>
                      </div>
                    ) : (
                      <div className="border border-border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-secondary/50 border-b border-border">
                              <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Name</th>
                              <th className="text-right py-2.5 px-4 font-medium text-muted-foreground">Ratio %</th>
                              <th className="text-right py-2.5 px-4 font-medium text-muted-foreground">Wt/Batch (lbs)</th>
                              <th className="text-right py-2.5 px-4 font-medium text-muted-foreground">Density</th>
                              <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ingredients.map((ing, idx) => (
                              <tr key={ing.id} className={`border-b border-border/50 last:border-0 ${idx % 2 === 0 ? "" : "bg-secondary/20"}`}>
                                <td className="py-2.5 px-4 text-foreground font-medium">
                                  <div className="flex items-center gap-2">
                                    <span className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: RATIO_COLORS_HEX[idx % RATIO_COLORS_HEX.length] }} />
                                    {ing.ingredient.name}
                                  </div>
                                </td>
                                <td className="py-2.5 px-4 text-right text-foreground tabular-nums">{ing.ratio_percent != null ? `${ing.ratio_percent}%` : "--"}</td>
                                <td className="py-2.5 px-4 text-right text-foreground tabular-nums">{ing.weight_per_batch_lbs != null ? ing.weight_per_batch_lbs.toLocaleString() : "--"}</td>
                                <td className="py-2.5 px-4 text-right text-foreground tabular-nums text-xs">
                                  {ing.ingredient.density_lbs_per_cuft != null ? (
                                    <span>{ing.ingredient.density_lbs_per_cuft} <span className="text-muted-foreground text-[10px]">lbs/ft³</span></span>
                                  ) : <span className="text-muted-foreground/40">--</span>}
                                </td>
                                <td className="py-2.5 px-4"><Badge variant={ing.ingredient.type === "ingredient" ? "default" : "warning"}>{ing.ingredient.type}</Badge></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className={`flex items-center justify-between p-3 rounded-lg border text-sm ${Math.abs(totalRatio - 100) < 0.01 ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400" : totalRatio > 100 ? "bg-red-500/10 border-red-500/20 text-red-400" : "bg-amber-500/10 border-amber-500/20 text-amber-400"}`}>
                      <span>Total Ratio</span>
                      <span className="font-semibold tabular-nums">{totalRatio.toFixed(1)}%</span>
                    </div>
                    <div className="space-y-3">
                      {editRows.map((row) => (
                        <div key={row.key} className="bg-secondary/30 border border-border rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <div className="text-muted-foreground pt-2"><GripVertical className="h-4 w-4" /></div>
                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="sm:col-span-2">
                                <label className="block text-xs text-muted-foreground mb-1">Ingredient</label>
                                <select
                                  value={row.ingredientId ?? ""}
                                  onChange={(e) => updateRow(row.key, "ingredientId", e.target.value ? Number(e.target.value) : null)}
                                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600/50 focus:border-forest-600"
                                >
                                  <option value="">Select ingredient...</option>
                                  {allIngredients.map((ing) => <option key={ing.id} value={ing.id}>{ing.name} {ing.type ? `(${ing.type})` : ""}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs text-muted-foreground mb-1">Ratio %</label>
                                <input type="number" min="0" max="100" step="0.1" value={row.ratioPercent ?? ""} onChange={(e) => updateRow(row.key, "ratioPercent", e.target.value ? Number(e.target.value) : null)} placeholder="0.0" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600/50 focus:border-forest-600 tabular-nums" />
                              </div>
                              <div>
                                <label className="block text-xs text-muted-foreground mb-1">Weight/Batch (lbs)</label>
                                <input type="number" min="0" step="1" value={row.weightPerBatchLbs ?? ""} onChange={(e) => updateRow(row.key, "weightPerBatchLbs", e.target.value ? Number(e.target.value) : null)} placeholder="0" className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600/50 focus:border-forest-600 tabular-nums" />
                              </div>
                            </div>
                            <button onClick={() => removeRow(row.key)} className="p-2 text-muted-foreground hover:text-red-400 transition-colors mt-1" title="Remove ingredient"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button onClick={addRow} className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-border rounded-lg text-sm text-muted-foreground hover:text-forest-400 hover:border-forest-600/40 transition-colors"><Plus className="h-4 w-4" /> Add Ingredient</button>
                    {saveError && <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm"><AlertCircle className="h-4 w-4 flex-shrink-0" /> {saveError}</div>}
                    <div className="flex items-center gap-3 pt-2">
                      <button onClick={saveRecipe} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">{saving ? <Spinner className="h-4 w-4" /> : <Save className="h-4 w-4" />} Save Recipe</button>
                      <button onClick={() => { setEditing(false); setSaveError(null); }} disabled={saving} className="px-5 py-2.5 border border-border rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">Cancel</button>
                    </div>
                  </div>
                )}

                {/* Supplier info */}
                {!editing && ingredients.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-muted-foreground mb-3">Supplier Info</h3>
                    <div className="space-y-2">
                      {ingredients.filter((ing) => ing.ingredient.supplier_name).map((ing) => (
                        <div key={ing.id} className="flex items-center justify-between text-sm px-3 py-2 bg-secondary/30 rounded-lg">
                          <span className="text-foreground">{ing.ingredient.name}</span>
                          <span className="text-muted-foreground flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> {ing.ingredient.supplier_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Slide-Over Panel (Ingredient Detail)
// ---------------------------------------------------------------------------

function IngredientSlideOver({
  open, onClose, ingredient, token, onUpdate,
}: {
  open: boolean;
  onClose: () => void;
  ingredient: IngredientRow | null;
  token: string | null;
  onUpdate?: (updated: IngredientRow) => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState<string | null>(null);
  const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);
  const [localDocs, setLocalDocs] = useState<IngredientDocument[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Sync local state when ingredient changes
  useEffect(() => {
    setLocalImageUrl(ingredient?.image_url || null);
    setLocalDocs(ingredient?.documents || []);
    setUploadError(null);
  }, [ingredient?.id, ingredient?.image_url, ingredient?.documents]);

  function handleBackdropClick(e: React.MouseEvent) {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
  }

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !ingredient || !token) return;
    setUploadingPhoto(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiFetch(`/api/ops/products/ingredients/${ingredient.id}/image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      const newUrl = data.url || data.image_url;
      setLocalImageUrl(newUrl);
      if (onUpdate) onUpdate({ ...ingredient, image_url: newUrl });
    } catch (err: any) { setUploadError(err.message || "Failed to upload photo"); }
    finally { setUploadingPhoto(false); if (photoInputRef.current) photoInputRef.current.value = ""; }
  }

  async function handleDocUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !ingredient || !token) return;
    setUploadingDoc(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiFetch(`/api/ops/products/ingredients/${ingredient.id}/documents`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      const newDoc = data.document || data;
      setLocalDocs((prev) => [...prev, newDoc]);
      if (onUpdate) onUpdate({ ...ingredient, documents: [...localDocs, newDoc] });
    } catch (err: any) { setUploadError(err.message || "Failed to upload document"); }
    finally { setUploadingDoc(false); if (docInputRef.current) docInputRef.current.value = ""; }
  }

  async function handleDeleteDoc(doc: IngredientDocument) {
    if (!ingredient || !token) return;
    setDeletingDoc(doc.path);
    setUploadError(null);
    try {
      const res = await apiFetch(`/api/ops/products/ingredients/${ingredient.id}/documents`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ path: doc.path }),
      });
      if (!res.ok) throw new Error("Delete failed");
      const updated = localDocs.filter((d) => d.path !== doc.path);
      setLocalDocs(updated);
      if (onUpdate) onUpdate({ ...ingredient, documents: updated });
    } catch (err: any) { setUploadError(err.message || "Failed to delete document"); }
    finally { setDeletingDoc(null); }
  }

  if (!ingredient) return null;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={handleBackdropClick}
      />
      <div
        ref={panelRef}
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-lg bg-background border-l border-border shadow-2xl transform transition-transform duration-300 ease-in-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-border">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-foreground truncate">{ingredient.name}</h2>
              <p className="text-sm text-muted-foreground">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${ingredient.type === "ingredient" ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"}`}>
                  {ingredient.type || "ingredient"}
                </span>
              </p>
            </div>
            <button aria-label="Close details" onClick={onClose} className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Photo */}
            {(!IS_LOCAL_DEMO || localImageUrl) && <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Photo</h3>
              <div className="relative group">
                {localImageUrl ? (
                  <img
                    src={localImageUrl}
                    alt={ingredient.name}
                    className="w-full max-w-[200px] h-auto rounded-xl border border-border object-cover"
                  />
                ) : (
                  <div className="w-full max-w-[200px] h-[200px] rounded-xl border border-dashed border-border bg-secondary/30 flex flex-col items-center justify-center text-muted-foreground">
                    <Camera className="h-8 w-8 mb-2 opacity-40" />
                    <span className="text-xs">No photo</span>
                  </div>
                )}
                {!IS_LOCAL_DEMO && <><input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
                <button
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-lg transition-colors disabled:opacity-50"
                >
                  {uploadingPhoto ? <Spinner className="h-3.5 w-3.5" /> : <Camera size={13} />}
                  {uploadingPhoto ? "Uploading..." : "Change Photo"}
                </button></>}
              </div>
            </div>}

            {/* Documents */}
            {!IS_LOCAL_DEMO && <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                <FileText size={14} /> Documents
              </h3>
              {localDocs.length > 0 && (
                <div className="space-y-2 mb-3">
                  {localDocs.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2.5 rounded-lg bg-secondary/50 border border-border">
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-forest-400 hover:text-forest-300 transition-colors min-w-0"
                      >
                        <FileText size={14} className="flex-shrink-0" />
                        <span className="truncate">{doc.name}</span>
                        <ExternalLink size={11} className="flex-shrink-0 opacity-50" />
                      </a>
                      <button
                        onClick={() => handleDeleteDoc(doc)}
                        disabled={deletingDoc === doc.path}
                        className="p-1.5 text-muted-foreground hover:text-red-400 transition-colors flex-shrink-0 disabled:opacity-50"
                        title="Delete document"
                      >
                        {deletingDoc === doc.path ? <Spinner className="h-3.5 w-3.5" /> : <Trash2 size={13} />}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input
                ref={docInputRef}
                type="file"
                className="hidden"
                onChange={handleDocUpload}
              />
              <button
                onClick={() => docInputRef.current?.click()}
                disabled={uploadingDoc}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-lg transition-colors disabled:opacity-50"
              >
                {uploadingDoc ? <Spinner className="h-3.5 w-3.5" /> : <Upload size={13} />}
                {uploadingDoc ? "Uploading..." : "Upload Document"}
              </button>
            </div>}

            {uploadError && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" /> {uploadError}
              </div>
            )}

            {/* Density */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                  <Weight size={14} /> Density
                </h3>
              </div>
              <div className="p-3 rounded-lg bg-secondary/50 border border-border">
                {ingredient.density_lbs_per_cuft != null ? (
                  <span className="text-lg font-semibold text-foreground tabular-nums">
                    {ingredient.density_lbs_per_cuft}{" "}
                    <span className="text-sm font-normal text-muted-foreground">lbs/ft³</span>
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">Not measured yet</span>
                )}
              </div>
              {!IS_LOCAL_DEMO && <p className="mt-2 text-[11px] text-muted-foreground/70 flex items-center gap-1.5">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/30" />
                Black metal 1 ft³ box tare weight: <span className="font-medium text-muted-foreground">12.08 lbs</span>
              </p>}
            </div>

            {/* Cost */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Cost</h3>
              <div className="p-3 rounded-lg bg-secondary/50 border border-border">
                {ingredient.cost_text || (ingredient.cost_per_ton != null ? (
                  <span className="text-foreground font-medium">${ingredient.cost_per_ton}/ton</span>
                ) : (
                  <span className="text-sm text-muted-foreground">Not set</span>
                ))}
              </div>
            </div>

            {/* Supplier */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                <Truck size={14} /> Supplier
              </h3>
              <div className="p-3 rounded-lg bg-secondary/50 border border-border">
                {ingredient.supplier_name ? (
                  <div>
                    <p className="text-foreground font-medium">{ingredient.supplier_name}</p>
                    {ingredient.supplier_contact && <p className="text-sm text-muted-foreground mt-0.5">{ingredient.supplier_contact}</p>}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">No supplier set</span>
                )}
              </div>
            </div>

            {/* Usage */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Used In</h3>
              <div className="p-3 rounded-lg bg-secondary/50 border border-border">
                <span className={`inline-flex items-center justify-center h-6 min-w-[1.5rem] rounded-full text-xs font-medium ${ingredient.product_count > 0 ? "bg-forest-600/20 text-forest-400" : "bg-secondary text-muted-foreground"}`}>
                  {ingredient.product_count}
                </span>
                <span className="text-sm text-muted-foreground ml-2">{ingredient.product_count === 1 ? "product" : "products"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Ingredient Modal
// ---------------------------------------------------------------------------

function IngredientModal({
  open, onClose, onSave, saving, error, initialData,
}: {
  open: boolean; onClose: () => void; onSave: (data: IngredientForm) => void;
  saving: boolean; error: string | null; initialData: IngredientForm | null;
}) {
  const [form, setForm] = useState<IngredientForm>({ ingrId: "", name: "", type: "ingredient", costText: "", costPerTon: "", supplierName: "", supplierContact: "", densityLbsPerCuft: "" });

  useEffect(() => {
    if (initialData) setForm(initialData);
    else setForm({ ingrId: "", name: "", type: "ingredient", costText: "", costPerTon: "", supplierName: "", supplierContact: "", densityLbsPerCuft: "" });
  }, [initialData, open]);

  function handleSubmit(e: React.FormEvent) { e.preventDefault(); onSave(form); }

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-background border border-border rounded-2xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground">{initialData ? "Edit Ingredient" : "Add Ingredient"}</h2>
            <button aria-label="Close details" onClick={onClose} className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"><X className="h-5 w-5" /></button>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.6fr)] gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">ID</label>
                <input
                  type="text"
                  value={form.ingrId}
                  onChange={(e) => setForm({ ...form, ingrId: e.target.value })}
                  placeholder="e.g. ING-01"
                  className="w-full bg-card border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600/50 focus:border-forest-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Name *</label>
                <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Green Waste Compost" className="w-full bg-card border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600/50 focus:border-forest-600" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-card border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600/50 focus:border-forest-600">
                <option value="ingredient">Ingredient</option>
                <option value="supply">Supply</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Cost Text</label>
                <input type="text" value={form.costText} onChange={(e) => setForm({ ...form, costText: e.target.value })} placeholder="e.g. $45/ton" className="w-full bg-card border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600/50 focus:border-forest-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Cost per Ton ($)</label>
                <input type="number" min="0" step="0.01" value={form.costPerTon} onChange={(e) => setForm({ ...form, costPerTon: e.target.value })} placeholder="0.00" className="w-full bg-card border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600/50 focus:border-forest-600 tabular-nums" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Supplier Name</label>
                <input type="text" value={form.supplierName} onChange={(e) => setForm({ ...form, supplierName: e.target.value })} placeholder="e.g. AZ Compost Co." className="w-full bg-card border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600/50 focus:border-forest-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Supplier Contact</label>
                <input type="text" value={form.supplierContact} onChange={(e) => setForm({ ...form, supplierContact: e.target.value })} placeholder="email or phone" className="w-full bg-card border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600/50 focus:border-forest-600" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                <span className="flex items-center gap-1.5">
                  <Weight size={14} />
                  Density (lbs/ft³)
                </span>
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">Fill the 1 ft³ box, drop test, weigh. Net weight = density. Box tare: <span className="font-medium">12.08 lbs</span></p>
              <input type="number" min="0" step="0.1" value={form.densityLbsPerCuft} onChange={(e) => setForm({ ...form, densityLbsPerCuft: e.target.value })} placeholder="e.g. 42.5" className="w-full bg-card border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-forest-600/50 focus:border-forest-600 tabular-nums" />
            </div>
            {error && <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm"><AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}</div>}
            <div className="flex items-center gap-3 pt-2">
              <button type="submit" disabled={saving || !form.name.trim()} className="flex items-center gap-2 px-5 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">{saving ? <Spinner className="h-4 w-4" /> : <Save className="h-4 w-4" />} {initialData ? "Update" : "Create"}</button>
              <button type="button" onClick={onClose} disabled={saving} className="px-5 py-2.5 border border-border rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export default function Products() {
  const { token, user } = useAuth();
  const canManage = ["manager", "admin", "owner", "platform_admin"].includes(user?.role || "");
  const params = useParams<{ productId?: string; ingredientId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialTab = searchParams.get("tab") === "ingredients" || params.ingredientId ? "ingredients" : "products";
  const [tab, setTab] = useState<"products" | "ingredients">(initialTab);

  function handleTabChange(newTab: "products" | "ingredients") {
    setTab(newTab);
    if (newTab === "ingredients") {
      navigate("/app/ops/products?tab=ingredients", { replace: true });
    } else {
      navigate("/app/ops/products", { replace: true });
    }
  }

  // Products state
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [productsSearch, setProductsSearch] = useState("");
  const [productSort, setProductSort] = useState<ProductSortField>("product_name");
  const [productSortDir, setProductSortDir] = useState<SortDir>("asc");
  const [certFilter, setCertFilter] = useState("all");

  // Ingredients state
  const [ingredients, setIngredients] = useState<IngredientRow[]>([]);
  const [ingredientsLoading, setIngredientsLoading] = useState(true);
  const [ingredientsError, setIngredientsError] = useState<string | null>(null);
  const [ingredientsSearch, setIngredientsSearch] = useState("");
  const [ingredientSort, setIngredientSort] = useState<IngredientSortField>("name");
  const [ingredientSortDir, setIngredientSortDir] = useState<SortDir>("asc");

  // Slide-over
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [slideOpen, setSlideOpen] = useState(false);

  // Ingredient slide-over
  const [selectedIngredient, setSelectedIngredient] = useState<IngredientRow | null>(null);
  const [ingredientSlideOpen, setIngredientSlideOpen] = useState(false);

  // Ingredient modal
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [ingredientModalOpen, setIngredientModalOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState<IngredientRow | null>(null);
  const [ingredientSaving, setIngredientSaving] = useState(false);
  const [ingredientModalError, setIngredientModalError] = useState<string | null>(null);

  // Fetch
  const fetchProducts = useCallback(async () => {
    if (!token) return;
    setProductsLoading(true); setProductsError(null);
    try {
      const res = await apiFetch("/api/ops/products", { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to fetch products (${res.status})`);
      const data = await res.json();
      setProducts(data.products || []);
    } catch (err: any) { setProductsError(err.message || "Failed to load products"); }
    finally { setProductsLoading(false); }
  }, [token]);

  const fetchIngredients = useCallback(async () => {
    if (!token) return;
    setIngredientsLoading(true); setIngredientsError(null);
    try {
      const res = await apiFetch("/api/ops/products/ingredients/all", { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to fetch ingredients (${res.status})`);
      const data = await res.json();
      setIngredients(data.ingredients || []);
    } catch (err: any) { setIngredientsError(err.message || "Failed to load ingredients"); }
    finally { setIngredientsLoading(false); }
  }, [token]);

  useEffect(() => { fetchProducts(); fetchIngredients(); }, [fetchProducts, fetchIngredients]);

  // Deep-link: auto-open product detail from URL param
  useEffect(() => {
    if (params.productId && products.length > 0 && !slideOpen) {
      const id = Number(params.productId);
      if (id) { setSelectedProductId(id); setSlideOpen(true); }
    }
  }, [params.productId, products.length]);

  // Deep-link: auto-open ingredient detail from URL param
  useEffect(() => {
    if (params.ingredientId && ingredients.length > 0 && !ingredientSlideOpen) {
      const id = Number(params.ingredientId);
      const ing = ingredients.find((i) => i.id === id);
      if (ing) { setSelectedIngredient(ing); setIngredientSlideOpen(true); handleTabChange("ingredients"); }
    }
  }, [params.ingredientId, ingredients.length]);

  // Product sorting + filtering
  function toggleProductSort(field: ProductSortField) {
    if (productSort === field) setProductSortDir(productSortDir === "asc" ? "desc" : "asc");
    else { setProductSort(field); setProductSortDir("asc"); }
  }

  const filteredProducts = useMemo(() => {
    let result = products;
    if (productsSearch.trim()) {
      const q = productsSearch.toLowerCase();
      result = result.filter((p) =>
        [p.product_name, p.product_id, ...(p.size_category_names || [])].join(" ").toLowerCase().includes(q)
      );
    }
    if (certFilter !== "all") {
      result = result.filter((p) => parseCertifications(p.certifications).includes(certFilter));
    }
    result = [...result].sort((a, b) => {
      let cmp = 0;
      switch (productSort) {
        case "product_name": cmp = a.product_name.localeCompare(b.product_name); break;
        case "product_id": cmp = a.product_id.localeCompare(b.product_id); break;
        case "ingredient_count": cmp = a.ingredient_count - b.ingredient_count; break;
      }
      return productSortDir === "asc" ? cmp : -cmp;
    });
    return result;
  }, [products, productsSearch, certFilter, productSort, productSortDir]);

  // Ingredient sorting + filtering
  function toggleIngredientSort(field: IngredientSortField) {
    if (ingredientSort === field) setIngredientSortDir(ingredientSortDir === "asc" ? "desc" : "asc");
    else { setIngredientSort(field); setIngredientSortDir("asc"); }
  }

  const filteredIngredients = useMemo(() => {
    let result = ingredients;
    if (ingredientsSearch.trim()) {
      const q = ingredientsSearch.toLowerCase();
      result = result.filter((i) => [i.name, i.supplier_name, i.type].join(" ").toLowerCase().includes(q));
    }
    result = [...result].sort((a, b) => {
      let cmp = 0;
      switch (ingredientSort) {
        case "ingr_id": cmp = (a.ingr_id || "").localeCompare(b.ingr_id || ""); break;
        case "name": cmp = a.name.localeCompare(b.name); break;
        case "type": cmp = (a.type || "").localeCompare(b.type || ""); break;
        case "cost_per_ton": cmp = (a.cost_per_ton || 0) - (b.cost_per_ton || 0); break;
        case "supplier_name": cmp = (a.supplier_name || "").localeCompare(b.supplier_name || ""); break;
        case "product_count": cmp = a.product_count - b.product_count; break;
        case "density_lbs_per_cuft": cmp = (a.density_lbs_per_cuft || 0) - (b.density_lbs_per_cuft || 0); break;
      }
      return ingredientSortDir === "asc" ? cmp : -cmp;
    });
    return result;
  }, [ingredients, ingredientsSearch, ingredientSort, ingredientSortDir]);

  // Stats
  const productsWithRecipes = products.filter((p) => p.ingredient_count > 0).length;
  const supplierCount = new Set(ingredients.filter((i) => i.supplier_name).map((i) => i.supplier_name)).size;

  // Unique certs for filter dropdown
  const allCerts = useMemo(() => {
    const s = new Set<string>();
    products.forEach((p) => parseCertifications(p.certifications).forEach((c) => s.add(c)));
    return Array.from(s).sort();
  }, [products]);

  // Handlers
  function openProduct(id: number) { setSelectedProductId(id); setSlideOpen(true); navigate(`/app/ops/products/${id}`, { replace: true }); }
  function closeSlide() { setSlideOpen(false); setTimeout(() => setSelectedProductId(null), 300); navigate("/app/ops/products", { replace: true }); }
  function openIngredientDetail(ing: IngredientRow) { setSelectedIngredient(ing); setIngredientSlideOpen(true); navigate(`/app/ops/products/ingredients/${ing.id}`, { replace: true }); }
  function closeIngredientSlide() { setIngredientSlideOpen(false); setTimeout(() => setSelectedIngredient(null), 300); navigate("/app/ops/products?tab=ingredients", { replace: true }); }
  function openAddIngredient() { setEditingIngredient(null); setIngredientModalError(null); setIngredientModalOpen(true); }
  function openEditIngredient(ing: IngredientRow) { setEditingIngredient(ing); setIngredientModalError(null); setIngredientModalOpen(true); }

  async function handleSaveIngredient(formData: IngredientForm) {
    if (!token) return;
    setIngredientSaving(true); setIngredientModalError(null);
    const body: Record<string, any> = {
      ingrId: formData.ingrId || null,
      name: formData.name, type: formData.type, costText: formData.costText || null,
      costPerTon: formData.costPerTon ? Number(formData.costPerTon) : null,
      supplierName: formData.supplierName || null, supplierContact: formData.supplierContact || null,
      densityLbsPerCuft: formData.densityLbsPerCuft ? Number(formData.densityLbsPerCuft) : null,
    };
    try {
      const isCreating = !editingIngredient;
      const res = isCreating
        ? await apiFetch("/api/ops/products/ingredients", { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(body) })
        : await apiFetch(`/api/ops/products/ingredients/${editingIngredient.id}`, { method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `Save failed (${res.status})`); }
      const result = await res.json();
      setIngredientModalOpen(false); await fetchIngredients();
      // After creating, auto-open the detail view so user can upload photos/documents
      if (isCreating && result.ingredient) {
        openIngredientDetail(result.ingredient);
      }
    } catch (err: any) { setIngredientModalError(err.message || "Failed to save ingredient"); }
    finally { setIngredientSaving(false); }
  }

  const thClass = "px-3 py-2.5 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground select-none";
  const thSortable = `${thClass} cursor-pointer hover:text-foreground transition-colors group`;

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="bg-card border border-border rounded-xl">
        <div className="flex flex-wrap items-center justify-between gap-3 px-3 pt-3 pb-2">
          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-lg font-bold text-foreground">Products & Ingredients</h1>
            {!productsLoading && tab === "products" && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
                <span><span className="font-semibold text-foreground">{products.length}</span> products</span>
                <span className="text-border">|</span>
                <span><span className="font-semibold text-foreground">{productsWithRecipes}</span> with recipes</span>
                <span className="text-border">|</span>
                <span><span className="font-semibold text-foreground">{ingredients.length}</span> ingredients</span>
              </div>
            )}
            {!ingredientsLoading && tab === "ingredients" && (
              <div className="flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
                <span><span className="font-semibold text-foreground">{ingredients.length}</span> total</span>
                <span className="text-border">|</span>
                <span><span className="font-semibold text-foreground">{supplierCount}</span> suppliers</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Tabs */}
            <div className="flex items-center gap-0.5 bg-secondary/50 p-0.5 rounded-lg">
              <button onClick={() => handleTabChange("products")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === "products" ? "bg-forest-600 text-white shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                <Package size={13} /> Products
              </button>
              <button onClick={() => handleTabChange("ingredients")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === "ingredients" ? "bg-forest-600 text-white shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                <FlaskConical size={13} /> Ingredients
              </button>
            </div>
            {tab === "products" && canManage && <button onClick={() => setProductModalOpen(true)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-forest-600 hover:bg-forest-700 text-white text-xs font-semibold rounded-lg"><Plus size={14} /> Add Product</button>}
            {tab === "ingredients" && (
              <button onClick={openAddIngredient} className="inline-flex items-center gap-1.5 px-4 py-2 bg-forest-600 hover:bg-forest-700 text-white text-xs font-semibold rounded-lg transition-colors">
                <Plus size={14} strokeWidth={2.5} /> Add Ingredient
              </button>
            )}
          </div>
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-2 px-3 pb-3">
          {tab === "products" && (
            <>
              <label className="flex-1 min-w-[180px] flex items-center gap-2 px-2.5 bg-background border border-border rounded-lg">
                <Search size={13} className="text-muted-foreground shrink-0" />
                <input value={productsSearch} onChange={(e) => setProductsSearch(e.target.value)} placeholder="Search product name, ID, size..." className="w-full py-1.5 text-xs bg-transparent outline-none text-foreground placeholder:text-muted-foreground/60" />
              </label>
              <select value={certFilter} onChange={(e) => setCertFilter(e.target.value)} className="h-8 px-2.5 bg-background border border-border rounded-lg text-xs text-foreground">
                <option value="all">All Certifications</option>
                {allCerts.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {filteredProducts.length !== products.length && (
                <span className="text-[11px] text-muted-foreground">{filteredProducts.length} of {products.length}</span>
              )}
            </>
          )}
          {tab === "ingredients" && (
            <>
              <label className="flex-1 min-w-[180px] flex items-center gap-2 px-2.5 bg-background border border-border rounded-lg">
                <Search size={13} className="text-muted-foreground shrink-0" />
                <input value={ingredientsSearch} onChange={(e) => setIngredientsSearch(e.target.value)} placeholder="Search ingredient, supplier..." className="w-full py-1.5 text-xs bg-transparent outline-none text-foreground placeholder:text-muted-foreground/60" />
              </label>
              {filteredIngredients.length !== ingredients.length && (
                <span className="text-[11px] text-muted-foreground">{filteredIngredients.length} of {ingredients.length}</span>
              )}
            </>
          )}
        </div>
      </div>

      {/* ================================================================= */}
      {/* PRODUCTS TABLE */}
      {/* ================================================================= */}
      {tab === "products" && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {productsLoading ? (
            <div className="p-12 text-center text-muted-foreground">Loading products...</div>
          ) : productsError ? (
            <div className="p-12 text-center">
              <AlertTriangle size={24} className="mx-auto text-red-500 mb-2" />
              <p className="text-sm text-red-600 dark:text-red-400">{productsError}</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-16 text-center">
              <Package size={40} className="mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">{productsSearch || certFilter !== "all" ? "No products match your filters." : "No products found."}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-muted/40">
                  <tr className="border-b border-border">
                    <th className={thSortable} onClick={() => toggleProductSort("product_name")}>
                      <span className="inline-flex items-center gap-1">Product <SortIcon field="product_name" sortField={productSort} sortDir={productSortDir} /></span>
                    </th>
                    <th className={thSortable} onClick={() => toggleProductSort("product_id")}>
                      <span className="inline-flex items-center gap-1">ID <SortIcon field="product_id" sortField={productSort} sortDir={productSortDir} /></span>
                    </th>
                    <th className={thClass}>Certifications</th>
                    <th className={thClass}>Sizes</th>
                    <th className={`${thSortable} text-center`} onClick={() => toggleProductSort("ingredient_count")}>
                      <span className="inline-flex items-center gap-1 justify-center">Ingredients <SortIcon field="ingredient_count" sortField={productSort} sortDir={productSortDir} /></span>
                    </th>
                    <th className={`${thClass} text-right`}>Density</th>
                    <th className={`${thClass} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p) => {
                    const certs = parseCertifications(p.certifications);
                    const sizes = p.size_category_names || [];
                    return (
                      <tr
                        key={p.id}
                        onClick={() => openProduct(p.id)}
                        className="border-b border-border/50 last:border-0 transition-colors cursor-pointer hover:bg-muted/30"
                      >
                        <td className="px-3 py-2 font-medium text-foreground max-w-[220px] truncate">{p.product_name}</td>
                        <td className="px-3 py-2 font-mono text-forest-600 dark:text-forest-400 text-xs">{p.product_id}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {certs.length > 0 ? certs.map((c, i) => <CertBadge key={i} label={c} />) : <span className="text-muted-foreground/40">-</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 max-w-[200px]">
                          {sizes.length > 0 ? (
                            <span className="text-muted-foreground text-xs truncate block" title={sizes.join(", ")}>
                              {sizes.slice(0, 3).join(", ")}{sizes.length > 3 ? ` +${sizes.length - 3}` : ""}
                            </span>
                          ) : <span className="text-muted-foreground/40">-</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center justify-center h-6 min-w-[1.5rem] rounded-full text-xs font-medium ${p.ingredient_count > 0 ? "bg-forest-600/20 text-forest-400" : "bg-secondary text-muted-foreground"}`}>
                            {p.ingredient_count}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-xs">
                          {p.density_lbs_per_cuft != null ? (
                            <span className="text-foreground">{p.density_lbs_per_cuft} <span className="text-muted-foreground text-[10px]">lbs/ft³</span></span>
                          ) : <span className="text-muted-foreground/40">-</span>}
                        </td>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end">
                            <button onClick={() => openProduct(p.id)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-colors" title="View details">
                              <Eye size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ================================================================= */}
      {/* INGREDIENTS TABLE */}
      {/* ================================================================= */}
      {tab === "ingredients" && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {ingredientsLoading ? (
            <div className="p-12 text-center text-muted-foreground">Loading ingredients...</div>
          ) : ingredientsError ? (
            <div className="p-12 text-center">
              <AlertTriangle size={24} className="mx-auto text-red-500 mb-2" />
              <p className="text-sm text-red-600 dark:text-red-400">{ingredientsError}</p>
            </div>
          ) : filteredIngredients.length === 0 ? (
            <div className="p-16 text-center">
              <FlaskConical size={40} className="mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">{ingredientsSearch ? "No ingredients match your search." : "No ingredients found."}</p>
              {!ingredientsSearch && (
                <button onClick={openAddIngredient} className="mt-3 text-sm text-forest-400 hover:text-forest-300 transition-colors">Add your first ingredient</button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="bg-muted/40">
                  <tr className="border-b border-border">
                    <th className={thClass} style={{ width: 44 }}></th>
                    <th className={thSortable} onClick={() => toggleIngredientSort("ingr_id")}>
                      <span className="inline-flex items-center gap-1">ID <SortIcon field="ingr_id" sortField={ingredientSort} sortDir={ingredientSortDir} /></span>
                    </th>
                    <th className={thSortable} onClick={() => toggleIngredientSort("name")}>
                      <span className="inline-flex items-center gap-1">Name <SortIcon field="name" sortField={ingredientSort} sortDir={ingredientSortDir} /></span>
                    </th>
                    <th className={thSortable} onClick={() => toggleIngredientSort("type")}>
                      <span className="inline-flex items-center gap-1">Type <SortIcon field="type" sortField={ingredientSort} sortDir={ingredientSortDir} /></span>
                    </th>
                    <th className={thSortable} onClick={() => toggleIngredientSort("cost_per_ton")}>
                      <span className="inline-flex items-center gap-1">Cost <SortIcon field="cost_per_ton" sortField={ingredientSort} sortDir={ingredientSortDir} /></span>
                    </th>
                    <th className={thSortable} onClick={() => toggleIngredientSort("supplier_name")}>
                      <span className="inline-flex items-center gap-1">Supplier <SortIcon field="supplier_name" sortField={ingredientSort} sortDir={ingredientSortDir} /></span>
                    </th>
                    <th className={`${thSortable} text-right`} onClick={() => toggleIngredientSort("density_lbs_per_cuft")}>
                      <span className="inline-flex items-center gap-1 justify-end">Density <SortIcon field="density_lbs_per_cuft" sortField={ingredientSort} sortDir={ingredientSortDir} /></span>
                    </th>
                    <th className={`${thSortable} text-center`} onClick={() => toggleIngredientSort("product_count")}>
                      <span className="inline-flex items-center gap-1 justify-center"># Products <SortIcon field="product_count" sortField={ingredientSort} sortDir={ingredientSortDir} /></span>
                    </th>
                    <th className={`${thClass} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIngredients.map((ing) => (
                    <tr key={ing.id} onClick={() => openIngredientDetail(ing)} className="border-b border-border/50 last:border-0 transition-colors cursor-pointer hover:bg-muted/30">
                      <td className="px-3 py-2">
                        {ing.image_url ? (
                          <img src={ing.image_url} alt={ing.name} className="h-8 w-8 rounded-full object-cover border border-border" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-secondary border border-border flex items-center justify-center text-xs font-semibold text-muted-foreground uppercase">
                            {ing.name.charAt(0)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">
                        {ing.ingr_id ? ing.ingr_id : <span className="text-muted-foreground/40">-</span>}
                      </td>
                      <td className="px-3 py-2 font-medium text-foreground">{ing.name}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${ing.type === "ingredient" ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"}`}>
                          {ing.type || "ingredient"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-foreground">
                        {ing.cost_text || (ing.cost_per_ton != null ? `$${ing.cost_per_ton}/ton` : <span className="text-muted-foreground/40">-</span>)}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground max-w-[160px] truncate">{ing.supplier_name || <span className="text-muted-foreground/40">-</span>}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {ing.density_lbs_per_cuft != null ? (
                          <span className="text-foreground">{ing.density_lbs_per_cuft} <span className="text-muted-foreground text-[10px]">lbs/ft³</span></span>
                        ) : <span className="text-muted-foreground/40">-</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex items-center justify-center h-6 min-w-[1.5rem] rounded-full text-xs font-medium ${ing.product_count > 0 ? "bg-forest-600/20 text-forest-400" : "bg-secondary text-muted-foreground"}`}>
                          {ing.product_count}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-0.5">
                          <button onClick={() => openIngredientDetail(ing)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-colors" title="View details">
                            <Eye size={14} />
                          </button>
                          <button onClick={() => openEditIngredient(ing)} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-colors" title="Edit">
                            <Pencil size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Dialog.Root open={productModalOpen} onOpenChange={setProductModalOpen}>
        <Dialog.Portal><Dialog.Overlay className="fixed inset-0 z-50 bg-black/50"/><Dialog.Content aria-describedby={undefined} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[calc(100%_-_2rem)] max-w-xl max-h-[90vh] overflow-y-auto rounded-xl bg-background border border-border p-6 shadow-xl"><Dialog.Title className="text-lg font-semibold mb-5">Add Product</Dialog.Title>
          <ProductRecordForm onCancel={() => setProductModalOpen(false)} onSaved={(product) => { setProductModalOpen(false); fetchProducts(); openProduct(product.id); }}/>
        </Dialog.Content></Dialog.Portal>
      </Dialog.Root>
      {/* Product Slide-over Panel */}
      <SlideOverPanel
        open={slideOpen} onClose={closeSlide} productId={selectedProductId}
        token={token} allIngredients={ingredients}
        onSaved={() => { fetchProducts(); fetchIngredients(); }}
      />

      {/* Ingredient Slide-over Panel */}
      <IngredientSlideOver
        open={ingredientSlideOpen} onClose={closeIngredientSlide}
        ingredient={selectedIngredient} token={token}
        onUpdate={(updated) => {
          setSelectedIngredient(updated);
          setIngredients((prev) => prev.map((i) => i.id === updated.id ? updated : i));
        }}
      />

      {/* Ingredient Modal */}
      <IngredientModal
        open={ingredientModalOpen} onClose={() => setIngredientModalOpen(false)}
        onSave={handleSaveIngredient} saving={ingredientSaving} error={ingredientModalError}
        initialData={editingIngredient ? {
          ingrId: editingIngredient.ingr_id || "",
          name: editingIngredient.name, type: editingIngredient.type || "ingredient",
          costText: editingIngredient.cost_text || "", costPerTon: editingIngredient.cost_per_ton != null ? String(editingIngredient.cost_per_ton) : "",
          supplierName: editingIngredient.supplier_name || "", supplierContact: editingIngredient.supplier_contact || "",
          densityLbsPerCuft: editingIngredient.density_lbs_per_cuft != null ? String(editingIngredient.density_lbs_per_cuft) : "",
        } : null}
      />
    </div>
  );
}
